/* eslint-disable react/jsx-props-no-spreading */
import { FC, ComponentProps, useState, useEffect } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Box, Typography } from '@altermaind/web/ui';
import { StyledRadio } from './styles';

type RadioItem = {
  label: string;
  value: string | number | boolean;
  index: number;
  selected: boolean;
};

type Props = {
  control: Control<FieldValues>;
  name: string;
  items: RadioItem[];
  additonalProps?: Record<string, unknown>;
  radioItemAlign?: 'vertical' | 'horizontal';
  wrapperProps?: ComponentProps<typeof Box>;
  containerRadioProps?: ComponentProps<typeof Box>;
  containerSelectedRadioOverrideProps?: ComponentProps<typeof Box>;
  isTotalClickable?: boolean;
} & ComponentProps<typeof StyledRadio>;

/**
 * WrappedRadio component.
 *
 * @component
 * @example
 * ```tsx
 * <WrappedRadio
 *   control={control}
 *   name="radioGroup"
 *   additonalProps={additionalProps}
 *   items={radioItems}
 *   {...props}
 * />
 * ```
 *
 * @param {Props} props - The component props.
 * @param {Control} props.control - The control object from react-hook-form.
 * @param {string} props.name - The name of the radio group.
 * @param {object} props.additonalProps - Additional rules for the radio group.
 * @param {RadioItem[]} props.items - The array of radio items.
 * @returns {JSX.Element} The rendered WrappedRadio component.
 */
export const WrappedRadio: FC<Props> = ({
  control,
  name,
  additonalProps,
  items,
  radioItemAlign = 'horizontal',
  wrapperProps = {},
  containerRadioProps = {},
  containerSelectedRadioOverrideProps = {},
  isTotalClickable = false,
  disabled,
  ...props
}) => {
  const [selected, setSelected] = useState<RadioItem[]>(items);

  useEffect(() => {
    setSelected(items);
  }, [items, disabled]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{ ...additonalProps }}
      render={({ field }) => (
        <Box
          mr='xs'
          display='flex'
          flexDirection={radioItemAlign === 'horizontal' ? 'row' : 'column'}
          {...wrapperProps}
        >
          {selected.map(item => (
            <Box
              key={`${Math.random()}_${item.value}`}
              display='flex'
              {...containerRadioProps}
              {...(item.selected ? containerSelectedRadioOverrideProps : {})}
              onClick={
                isTotalClickable
                  ? () => {
                      if (disabled) {
                        return;
                      }
                      field.onChange(item.value);
                      setSelected(
                        selected.map(i => ({
                          ...i,
                          selected: i.index === item.index,
                        })),
                      );
                    }
                  : undefined
              }
            >
              <StyledRadio
                {...field}
                {...props}
                labelID={item.index as never as string}
                checked={item.selected}
                onClick={
                  !isTotalClickable
                    ? () => {
                        if (disabled) {
                          return;
                        }
                        field.onChange(item.value);
                        setSelected(
                          selected.map(i => ({
                            ...i,
                            selected: i.index === item.index,
                          })),
                        );
                      }
                    : undefined
                }
              />
              <Typography
                style={{ cursor: isTotalClickable ? 'pointer' : 'default' }}
                ml='xxs'
                mr='xs'
                variant='p2/light'
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    />
  );
};
